import React, { useEffect, useMemo, useRef, useState } from 'react';
import { WebSocketContext } from './WebSocketContext';
import { retrieveCachedUserDetails } from '../thunks/session_thunks';
import posthog from 'posthog-js';
import { WEB_SOCKET_FEATURE_FLAG } from '../utils/WebSocketUtils';

const WS_URL = 'wss://ws.useopenwrench.com';

const PING_INTERVAL = 30000;
const RECONNECT_INTERVAL = 3000;

export const WebSocketProvider = ({ children }) => {
	const socketRef = useRef(null);
	const reconnectTimerRef = useRef(null);
	const pingIntervalRef = useRef(null);

	const [isConnected, setIsConnected] = useState(false);
	const [socket, setSocket] = useState(null);

	const [enabled, setEnabled] = useState(posthog.isFeatureEnabled(WEB_SOCKET_FEATURE_FLAG));

	useEffect(() => {
		let isActive = true;

		const update = () => {
			if (!isActive) return;
			setEnabled(posthog.isFeatureEnabled(WEB_SOCKET_FEATURE_FLAG));
		};

		posthog.onFeatureFlags(update);

		return () => {
			isActive = false;
		};
	}, []);

	const connect = () => {
		const cachedUserDetails = retrieveCachedUserDetails(['token', 'userType']);
		const token = cachedUserDetails.token;
		const userType = cachedUserDetails.userType;
		if (!token) return;

		const ws = new WebSocket(`${WS_URL}/${userType}/connect?token=${token}`);
		socketRef.current = ws;
		setSocket(ws); // force React to propagate the new socket

		ws.onopen = () => {
			setIsConnected(true);

			if (reconnectTimerRef.current) {
				clearTimeout(reconnectTimerRef.current);
				reconnectTimerRef.current = null;
			}

			pingIntervalRef.current = setInterval(() => {
				if (ws.readyState === WebSocket.OPEN) {
					ws.send('ping');
				}
			}, PING_INTERVAL);
		};

		ws.onclose = () => {
			setIsConnected(false);

			if (pingIntervalRef.current) {
				clearInterval(pingIntervalRef.current);
				pingIntervalRef.current = null;
			}

			if (enabled) {
				reconnectTimerRef.current = setTimeout(() => {
					connect();
				}, RECONNECT_INTERVAL);
			}
		};

		ws.onerror = (err) => {
			ws.close();
		};
	};

	useEffect(() => {
		if (!enabled) {
			if (socketRef.current) socketRef.current.close();
			if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
			if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);

			setIsConnected(false);
			setSocket(null);
			console.log("Won't connect to Websocket for this user...");
			return;
		}

		console.log('WS connection initiating');

		connect();

		return () => {
			if (socketRef.current) socketRef.current.close();
			if (reconnectTimerRef.current) clearTimeout(reconnectTimerRef.current);
			if (pingIntervalRef.current) clearInterval(pingIntervalRef.current);
		};
	}, [enabled]);

	const sendMessage = (msg) => {
		if (socketRef.current && isConnected) {
			socketRef.current.send(JSON.stringify(msg));
		}
	};

	const value = useMemo(
		() => ({
			socket: socketRef.current,
			isConnected,
			sendMessage,
		}),
		[socket, isConnected]
	);

	return <WebSocketContext.Provider value={value}>{children}</WebSocketContext.Provider>;
};
